import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductCardProps } from '@components/molecules/ProductCard';
import { FilterSidebarProducts } from '@components/molecules/filters/FilterSidebarProducts';
import { ResultsContainer } from '@components/organisms/ResultsContainer';
import { StickyHeader } from '@components/molecules/StickyHeader/StickyHeader';
import { MarketplaceAdvertising } from '@components/molecules/MarketplaceAdvertising';
import { Footer } from '@components/molecules/Footer';
import { Space } from '@components/atoms/Space';
import { useFilters } from '@hooks/useFilters';
import { useInfiniteScroll } from '@hooks/useInfiniteScroll';
import { useData } from '@hooks/useData';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { updateUrlSearchParams } from '@utils/search';
import { ResultsType } from '@types';

export interface ProductsSearchResultsProps {
    searchQuery: string;
}

export const ProductsSearchResults: FC<ProductsSearchResultsProps> = ({ searchQuery }) => {
    const { navigate } = useCustomNavigate();
    const { products, isLoadingProducts } = useData();
    const { results } = useFilters();
    const { pageToRequest, checkPageToRequest, observerRef } = useInfiniteScroll();
    const { t } = useTranslation();

    const translations = {
        orderNameAsc: t('sort-options.name_asc'),
        orderCreatedAtDesc: t('sort-options.created_at_desc'),
        orderPriceAsc: t('sort-options.price_asc'),
        orderPriceDesc: t('sort-options.price_desc'),
    };

    const handleOrderChange = (value: string) => {
        const updatedUrl = updateUrlSearchParams({ order: [value] });
        navigate(updatedUrl);
    };

    return (
        <>
            <StickyHeader />

            <ResultsContainer
                type={ResultsType.products}
                results={results as ProductCardProps[]}
                scrollObserverRef={observerRef}
                filters={
                    <FilterSidebarProducts
                        isSearchResultsPage={true}
                        pageToRequest={pageToRequest}
                        checkPageToRequest={checkPageToRequest}
                    />
                }
                total={products?.pagination?.total || 0}
                order={{
                    options: [
                        { value: 'name_asc', text: translations.orderNameAsc },
                        { value: 'price_asc', text: translations.orderPriceAsc },
                        { value: 'price_desc', text: translations.orderPriceDesc },
                        { value: 'created_at_desc', text: translations.orderCreatedAtDesc },
                    ],
                    onChange: handleOrderChange,
                }}
                searchQuery={searchQuery}
                loading={isLoadingProducts}
            />

            <Space spaceBreakpoints={{ xs: { space: 50 }, md: { space: 100 } }} />

            <MarketplaceAdvertising />

            <Footer mode="normal" />
        </>
    );
};
